import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons"
import {
  Button,
  Card,
  Modal,
  Select,
  Space,
  Table,
  TableColumnsType,
  TablePaginationConfig,
  TableProps,
  Tabs,
  Typography,
  message,
} from "antd"
import queryString from "query-string"
import { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import Filters, { FilterComponent } from "~/components/Filters"
import { USERS } from "~/constants/Paths"
import { t } from "~/languages/i18n"
import { UserUpdatePayload } from "~/types/types"
import { normalizeText } from "~/utils/helper"
import userService from "../services/userService"

function UsersListing() {
  const isLoading = userService().isLoading
  const navigate = useNavigate()
  const { Title } = Typography
  const [loading, setLoading] = useState(false)
  const filterRef = useRef<{ filterFunction: () => void } | null>(null)
  const [userNameFill, setUserNameFill] = useState<{ value: string | number; label: string }[]>([])
  const [emailOptions, setEmailOptions] = useState<{ value: string; label: string }[]>([])
  const [defaultFilterValues, setDefaultFilterValues] = useState<{ [key: string]: any } | null>(null)
  const [sortPaginationValues, setSortPaginationValues] = useState<{ [key: string]: string } | null>(null)
  const [dataSource, setDataSource] = useState<UserUpdatePayload[]>([])
  const [totalUsers, setTotalUsers] = useState(0)
  const [filterValues, setFilterValues] = useState<{ [key: string]: string } | null>(null)
  const [adminCount, setAdminCount] = useState(0)
  const [filterUser, setFilterUser] = useState("all")
  const [needRefresh, setNeedRefresh] = useState<boolean>(false)
  const [bulkAction, setBulkAction] = useState<string>("")
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])

  const userServices = userService()

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(selectedRowKeys)
    },
  }

  const handleBulkDelete = async () => {
    try {
      setLoading(true)
      const ids = selectedRowKeys.map(id => Number(id))
      await Promise.all(ids.map(id => userServices.deleteUser(id)))
      message.success(t("Users deleted successfully!"))
      setSelectedRowKeys([])
      fetchUsers()
      setLoading(false)
    } catch (error) {
      message.error(t("An error occurred while deleting users."))
    } finally {
      setLoading(false)
    }
  }

  const handleApplyBulkAction = () => {
    if (bulkAction === "delete" && selectedRowKeys.length > 0) {
      Modal.confirm({
        title: t("Confirm Delete"),
        content: t("Are you sure you want to delete the selected users?"),
        okText: t("Delete"),
        cancelText: t("Cancel"),
        onOk: handleBulkDelete,
      })
    } else if (selectedRowKeys.length === 0) {
      message.warning(t("Please select users to apply the action."))
    } else {
      message.warning(t("Please select a valid bulk action."))
    }
  }

  const fetchUsers = async (searchTerms: { [key: string]: string } = {}) => {
    setLoading(true)

    try {
      const updatePayload = await userServices.fetchAllUsers(searchTerms)

      if (updatePayload && updatePayload.data.length > 0) {
        setDataSource(updatePayload.data)
        setTotalUsers(updatePayload.data.length)
        setAdminCount(updatePayload.data.filter((data: { role: { id: number } }) => data.role.id === 1).length)
        setUserNameFill(
          updatePayload.data.map((user: { id: number; username: string }) => ({
            value: user.id,
            label: user.username,
          })),
        )

        const uniqueEmails: string[] = Array.from(
          new Set<string>(updatePayload.data.map((user: { email: string }) => user.email)),
        )

        setEmailOptions(
          uniqueEmails.map((email: string) => ({
            value: email,
            label: email,
          })),
        )
        setLoading(false)
      } else {
        setLoading(false)
      }
    } catch (error) {
      message.error(t("Error fetching users"))
    }
  }

  const handleTabChange = (key: string) => {
    setFilterUser(key)
  }

  const handleCreateUser = () => {
    const createPath = USERS.CREATE.PATH
    navigate(createPath)
  }

  const handleEditUser = (id: number) => {
    const editPath = USERS.EDIT.PATH.replace(":idUser", id.toString())
    navigate(editPath)
  }

  const handleDeleteUser = async (id: number) => {
    Modal.confirm({
      title: t("Confirm Delete"),
      content: t("Are you sure you want to delete this user?"),
      okText: t("Delete"),
      cancelText: t("Cancel"),
      onOk: async () => {
        try {
          setLoading(true)
          await userServices.deleteUser(id)

          fetchUsers()
          message.success(t("User deleted successfully!"))
        } catch {
          message.error(t("Failed to delete user. Please try again."))
        }
      },
    })
  }

  const onSearch = useCallback(
    (filters: object) => {
      const values: { [key: string]: string } = {}

      Object.keys(filters).forEach(key => {
        const value = filters[key as keyof typeof filters]
        if (typeof value === "string") {
          values[key] = value
        }
      })

      if (!values) {
        message.error(t("Values are undefined"))
        return
      }

      let newValue = { ...values, ...sortPaginationValues }

      if (values.search) {
        newValue = { ...newValue, search: normalizeText(values.search) }
      }

      const searchParams = queryString.stringify(newValue, { encode: false })
      const newUrl = searchParams.length > 0 ? `${location.pathname}?${searchParams}` : location.pathname
      navigate(newUrl)
    },
    [sortPaginationValues, navigate],
  )

  useEffect(() => {
    const searchParams = queryString.parse(location.search)
    const filterValues: { [key: string]: string } = {}
    const sortPaginationValues: { [key: string]: string } = {}

    for (const key of Object.keys(searchParams)) {
      const value = searchParams[key]
      if (value && typeof value === "string") {
        if (key === "page" || key === "limit" || value === "asc" || value === "desc") {
          sortPaginationValues[key] = value
        } else {
          filterValues[key] = value
        }
      }
    }

    setSortPaginationValues(sortPaginationValues)
    setFilterValues(filterValues)
    setDefaultFilterValues(filterValues)
  }, [location.search])

  useEffect(() => {
    if (!filterValues && !sortPaginationValues) return
    const params = { ...filterValues, ...sortPaginationValues }
    fetchUsers(params)
    if (filterUser === "admin") {
      fetchUsers({ role: "1" })
    }
  }, [filterValues, needRefresh, filterUser])

  const onChange: TableProps<UserUpdatePayload>["onChange"] = (pagination: TablePaginationConfig) => {
    setSortPaginationValues({
      page: pagination.current ? pagination.current.toString() : "1",
      pageSize: pagination.pageSize?.toString() || "10",
    })

    fetchUsers({
      ...filterValues,
      ...sortPaginationValues,
    })
  }

  const onRefresh = () => {
    if (filterRef.current) {
      filterRef.current.filterFunction()
    }
    setSortPaginationValues(null)
    setNeedRefresh(!needRefresh)
  }

  const columns: TableColumnsType<UserUpdatePayload> = [
    {
      title: <strong>ID</strong>,
      dataIndex: "id",
      key: "id",
      width: "5%",
      showSorterTooltip: false,
      sorter: (a: { id: number }, b: { id: number }) => a.id - b.id,
    },
    {
      title: <strong>{t("Username")}</strong>,
      dataIndex: "username",
      key: "username",
    },
    {
      title: <strong>Email</strong>,
      dataIndex: "email",
      key: "email",
    },
    {
      title: <strong>{t("Role")}</strong>,
      dataIndex: "role",
      key: "role",
      render: (role: { id: number; name: string }) => {
        return <>{role?.name || "Subscriber"}</>
      },
    },
    {
      title: <strong>{t("Action")}</strong>,
      dataIndex: "id",
      key: "action",
      width: 250,
      render: (id: number) => (
        <>
          <Space size="middle" className="justify-center items-center">
            <Button
              onClick={() => handleEditUser(id)}
              type="primary"
              icon={<EditOutlined />}
              className="bg-blue-500 px-4 py-2 rounded"
            >
              {t("Edit")}
            </Button>
            <Button danger type="primary" icon={<DeleteOutlined />} className="" onClick={() => handleDeleteUser(id)}>
              <span className="">{t("Delete")}</span>
            </Button>
          </Space>
        </>
      ),
    },
  ]

  return (
    <div className="p-4 ">
      <div className="flex py-1 items-center justify-between">
        <div className="flex space-x-4 items-center">
          <Title level={2} className="m-0">
            {t("Users")}
          </Title>
          <Button onClick={() => handleCreateUser()} icon={<PlusOutlined />} color="primary" variant="outlined">
            {t("Add New User")}
          </Button>
        </div>
      </div>
      <div className="flex justify-between mt-3">
        <Tabs
          defaultActiveKey="all"
          activeKey={filterUser}
          onChange={handleTabChange}
          items={[
            { label: `${t("All")} (${totalUsers})`, key: "all" },
            { label: `${t("Administrator")} (${adminCount})`, key: "admin" },
          ]}
        />
      </div>
      <div className="items-center">
        <Space className="space-x-5 flex justify-between">
          <Space className="space-x-3">
            <Select
              defaultValue={t("Bulk Action")}
              style={{ width: 170 }}
              onChange={value => setBulkAction(value)}
              options={[
                { value: "all", label: t("Bulk Action") },
                { value: "delete", label: t("Delete") },
              ]}
            />
            <Button onClick={handleApplyBulkAction}>{t("Apply")}</Button>
          </Space>
          <Space>
            <Button onClick={onRefresh} icon={<ReloadOutlined />}>
              {t("Refresh")}
            </Button>
          </Space>
        </Space>
      </div>
      <Card type="inner" style={styles.card}>
        <Filters
          filterRef={filterRef}
          defaultFilterValues={defaultFilterValues}
          onSubmit={onSearch}
          filters={
            [
              {
                label: t("Username"),
                placeholder: t("Find Username"),
                name: "id",
                type: "select-search",
                options: userNameFill,
              },
              {
                label: t("Email"),
                placeholder: t("Find Email"),
                name: "email",
                type: "select-search",
                options: emailOptions,
              },
            ] as FilterComponent[]
          }
        />
      </Card>
      <Table
        className="mt-3 border border-gray-300 custom-table"
        rowKey={record => record.id}
        rowSelection={rowSelection}
        columns={columns}
        loading={loading}
        dataSource={filterUser === "all" ? dataSource : dataSource.filter(data => data.role.id === 1)}
        onChange={onChange}
        pagination={{
          pageSize: 10,
        }}
        scroll={{ x: 1200 }}
        rowClassName={(record, index) => (index % 2 === 0 ? "bg-[#f6f7f7] " : "bg-white")}
      />
    </div>
  )
}

export default UsersListing

const styles = {
  title: {
    marginTop: 0,
    marginBottom: 0,
  },
  card: {
    marginTop: "1.25rem",
    marginBottom: "1.25rem",
  },
}
